import React, { useMemo } from 'react'
import { getEnabledOptions, getOptionsConfig } from '@rawgraphs/rawgraphs-core'
import styles from './CanvasLab.module.scss'

// This is deliberately a small, route-local view over Lugsail's existing
// option schema. It does not define defaults or chart behaviour: those remain
// owned by each RAWGraphs chart and the shared chart snapshot service.
const isCanvasControl = (optionId, definition) => (
  definition.type === 'text' && optionId.toLowerCase().startsWith('sort')
) || [
  'padding',
  'barsOrientation',
  'showGrid',
  'showLegend',
  'showSeriesLabels',
  'showLabels',
  'autoHideLabels',
].includes(optionId)

function optionLabel(definition, optionId) {
  return definition.label || optionId.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase())
}

function selectOptions(options = []) {
  return options.filter(Boolean).map((option) => (
    typeof option === 'string'
      ? { label: option, value: option }
      : { label: option.label || option.value, value: option.value }
  ))
}

export default function CanvasVisualControls({ chart, mapping, visualOptions, onChange, hasLegendData = false }) {
  const controls = useMemo(() => {
    try {
      const config = getOptionsConfig(chart?.visualOptions) || {}
      const enabled = getEnabledOptions(config, visualOptions || {}, mapping || {}) || {}

      return Object.entries(config)
        .filter(([optionId, definition]) => (
          isCanvasControl(optionId, definition)
          && enabled[optionId]
          // A legend without a colour dimension is a valid empty SVG group in
          // RAWGraphs, but is not a useful Canvas control.
          && (optionId !== 'showLegend' || hasLegendData)
        ))
        .slice(0, 6)
    } catch (error) {
      // Production option predicates can depend on dimensions not exposed in
      // this deliberately narrow inspector. Skip them instead of breaking it.
      return []
    }
  }, [chart, mapping, visualOptions])

  if (!controls.length) return null

  return <section className={styles.visualControls} aria-label="Chart display controls">
    <h3>Chart display</h3>
    {controls.map(([optionId, definition]) => {
      const value = visualOptions?.[optionId] ?? definition.default
      const label = optionLabel(definition, optionId)

      if (definition.type === 'boolean') {
        return <label key={optionId} className={styles.toggleControl}>
          <span>{label}</span>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => onChange((current) => ({ ...current, [optionId]: event.target.checked }))}
          />
        </label>
      }

      if (definition.type === 'number') {
        return <label key={optionId}>
          {label}
          <input
            type="number"
            value={value ?? ''}
            min={definition.min}
            max={definition.max}
            step={definition.step || 1}
            onChange={(event) => onChange((current) => ({ ...current, [optionId]: Number(event.target.value) }))}
          />
        </label>
      }

      if (definition.type === 'text' && definition.options) {
        return <label key={optionId}>
          {label}
          <select
            value={value ?? ''}
            onChange={(event) => onChange((current) => ({ ...current, [optionId]: event.target.value }))}
          >
            {selectOptions(definition.options).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      }

      return null
    })}
  </section>
}
